/**
 * InsightBell — Klingel-Icon im App-Header mit Suggestion-Counter.
 *
 * Zwei Betriebsmodi:
 *   - `ui_ai_first === false` (Default): Klick öffnet Popover mit
 *     AiSuggestionsInbox (unverändertes Verhalten, Regressions-sicher).
 *   - `ui_ai_first === true`:  Kein Popover. Klick scrollt im Chat-Feed
 *     zur neuesten ungelesenen Smart Card via ChatFeedScrollContext.
 *
 * Badge-Counter (`pendingCount`) ist in beiden Modi identisch.
 */

import React from 'react';
import { Bell } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAiCompanion } from '@/contexts/AiCompanionContext';
import { useChatFeedScroll } from '@/contexts/ChatFeedScrollContext';
import { useFeatures } from '@/hooks/useFeatures';
import { AiSuggestionsInbox } from './AiSuggestionsInbox';

export const InsightBell: React.FC = () => {
  const { pendingCount, suggestions } = useAiCompanion();
  const { scrollToSuggestion } = useChatFeedScroll();
  const { hasFeature } = useFeatures();
  const isAiFirst = hasFeature('ui_ai_first');

  const hasUrgent = pendingCount > 0;

  // ── AI-First-Modus: Scroll zu neuester Card, kein Popover ────────────────
  if (isAiFirst) {
    const handleClick = () => {
      // Neueste Suggestion nach created_at desc
      const newest = [...suggestions].sort((a, b) =>
        b.created_at.localeCompare(a.created_at),
      )[0];
      if (newest) {
        scrollToSuggestion(newest.id);
      }
    };

    return (
      <Button
        variant="ghost"
        size="icon"
        aria-label={`KI-Vorschläge${hasUrgent ? ` (${pendingCount})` : ''}`}
        className="relative"
        onClick={handleClick}
        data-testid="insight-bell-ai-first"
      >
        <Bell
          className={cn(
            'h-5 w-5',
            hasUrgent ? 'text-amber-600' : 'text-muted-foreground',
          )}
        />
        {hasUrgent && (
          <Badge
            variant="destructive"
            className="absolute -right-1 -top-1 h-5 min-w-5 justify-center px-1 text-xs"
          >
            {pendingCount > 9 ? '9+' : pendingCount}
          </Badge>
        )}
      </Button>
    );
  }

  // ── Standard-Modus: Popover ───────────────────────────────────────────────
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`KI-Vorschläge${hasUrgent ? ` (${pendingCount})` : ''}`}
          className="relative"
          data-testid="insight-bell-popover"
        >
          <Bell
            className={cn(
              'h-5 w-5',
              hasUrgent ? 'text-amber-600' : 'text-muted-foreground',
            )}
          />
          {hasUrgent && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 h-5 min-w-5 justify-center px-1 text-xs"
            >
              {pendingCount > 9 ? '9+' : pendingCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0">
        <AiSuggestionsInbox />
      </PopoverContent>
    </Popover>
  );
};
